// frontend/src/utils/appInsights.ts
import { ApplicationInsights, IExceptionTelemetry, IEventTelemetry } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

const reactPlugin = new ReactPlugin();

const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;

const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    extensions: [reactPlugin],
    enableAutoRouteTracking: true,
    disableFetchTracking: false,
    enableCorsCorrelation: true,
    // Do not collect until enableAppInsights() after analytics consent.
    disableTelemetry: true,
  },
});

let loaded = false;

function telemetryEnabled(): boolean {
  return Boolean(connectionString && loaded && !appInsights.config.disableTelemetry);
}

/**
 * Load App Insights only after analytics cookie consent (RODO).
 * Safe to call multiple times.
 */
export function enableAppInsights(): void {
  if (!connectionString) return;
  try {
    if (!loaded) {
      appInsights.loadAppInsights();
      loaded = true;
    }
    appInsights.config.disableTelemetry = false;
    appInsights.trackPageView();
  } catch (err) {
    console.warn('[AppInsights] enable failed:', err);
  }
}

/** Stop sending telemetry after decline / revoke (session). */
export function disableAppInsights(): void {
  if (!connectionString) return;
  try {
    appInsights.config.disableTelemetry = true;
  } catch {
    /* ignore */
  }
}

/** Client exception — no-op when consent declined / AI not loaded. Always console.error. */
export function trackClientException(
  error: unknown,
  properties: Record<string, string | number | boolean | undefined> = {}
): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const props: Record<string, string> = {};
  for (const [k, v] of Object.entries(properties)) {
    if (v !== undefined) props[k] = String(v);
  }
  console.error('[client-exception]', props.errorId || '', err);

  if (!telemetryEnabled()) return;
  try {
    const payload: IExceptionTelemetry = {
      exception: err,
      properties: props,
    };
    appInsights.trackException(payload);
  } catch {
    /* ignore */
  }
}

/** Lightweight custom event for ops (API fail, chunk fail). */
export function trackClientEvent(
  name: string,
  properties: Record<string, string | number | boolean | undefined> = {}
): void {
  if (!telemetryEnabled()) return;
  try {
    const props: Record<string, string> = {};
    for (const [k, v] of Object.entries(properties)) {
      if (v !== undefined) props[k] = String(v);
    }
    const payload: IEventTelemetry = { name, properties: props };
    appInsights.trackEvent(payload);
  } catch {
    /* ignore */
  }
}

export { reactPlugin, appInsights };
